import React, { useEffect, Suspense, lazy } from 'react';
import { Spin } from 'antd';
import { Switch, Route, useRouteMatch, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Dashboard from './dashboard';
import withAdminLayout from '../../layout/withAdminLayout';
import useFetchClientConfig from '../../api/FetchClientConfig';
import { updateClientConfig } from '../../redux/clientConfig/actionCreators';
import FormLibrary from '../../container/formLibrary/FormLibrary';
import { useAuth } from '../../contexts/AuthContext';
import { handleRoleBasedRouting, getPrimaryRole } from '../../utility/roleRoutingUtils';

const LeaderboardRoutes = lazy( () => import( './leaderboard' ) );
const onBoardingRoutes = lazy( () => import( './clientRoutes/clientRoute' ) );
const addNewClientRoute = lazy( () => import( './clientRoutes/onBoardingRoute' ) );
const instituteRoutes = lazy( () => import( './instituteRoutes/instituteRoutes' ) );
const studentRoutes = lazy( () => import( './studentRoutes/studentRoutes' ) )
const AddNewClientConfig = lazy( () => import( '../../container/clientTab/components/config' ) );
const AdminClientConfig = lazy( () => import( '../../container/clientTab/components/AdminClientConfig' ) );
const LeadsTable = lazy( () => import( './LeadsRoutes/leadRoutes' ) )
const PromoCodesTable = lazy( () => import( './paymentRoutes/promoCodeRoutes' ) )
const TransactionsTable = lazy( () => import( './paymentRoutes/transactionRoutes' ) )
const AllFormTable = lazy( () => import( './formRoutes/formRoutes' ) )
const UTMRoutes = lazy( () => import( './UTMRoutes/utmRoutes' ) )
const BookingRoute = lazy( () => import( './bookingsRoutes/bookingsRoutes' ) )
const FlowRoute = lazy( () => import( './flowRoutes/flowRoutes' ) )
const MyResponses = lazy( () => import( './myResponsesRoutes/myResponsesRoutes' ) )
const MyProfileRoutes = lazy( () => import( './myProfile/myprofileRoutes' ) )
const CampaignsRoutes = lazy( () => import( './campaignRoutes/CampaignRoutes' ) )
const MentorRoutes = lazy( () => import( './mentorRoutes/mentorRoute' ) )
const CouponCodeRoutes = lazy( () => import( './couponCodeRoutes/couponCodeRoutes' ) )
const TestSeriesRoutes = lazy( () => import( './testSeriesRoutes/TestSeriesRoutes' ) )
const DashbordMarketingCards = lazy( () => import( './dashboardMarketingCardRoutes/DashbordMarketingCards' ) )
const ApplicationFormRoutes = lazy( () => import( './applicationFormRoutes/ApplicationFormRoutes' ) )
const UserAssessmentResponsesRoutes = lazy( () => import( './userAssessmentResponsesRoutes/UserAssessmentResponsesRoutes' ) )
const DynamicIframeRoute = lazy( () => import( './iframeRoutes/DynamicIframeRoute' ) )

function Admin() {
    const { path } = useRouteMatch();
    const dispatch = useDispatch();
    const history = useHistory();
    const { user, isAuthenticated } = useAuth();
    
    // Get user role from user data using utility function
    const userRole = getPrimaryRole(user);
    const clientId = user?.client_id;
    
    // Handle role-based routing using utility function
    useEffect(() => {
        if (user) {
            handleRoleBasedRouting(user, history.push);
        }
    }, [user, history]);

    const { 
        response: clientConfigDetails, 
        isLoading: clientConfigLoading,
        error: clientConfigError,
        FetchClientConfig,
    } = useFetchClientConfig(userRole);

    useEffect(() => {
        const fetchConfig = async () => {
            if (!isAuthenticated || !user) return;
            try {
                await FetchClientConfig();
            } catch (error) {
                console.error('Error fetching client config:', error);
            }
        };

        fetchConfig();
    }, [isAuthenticated, user, userRole, clientId]);

    useEffect(() => {
        if (clientConfigDetails?.data) {
            console.log('Updating client config in Redux:', clientConfigDetails.data);
            dispatch(updateClientConfig(clientConfigDetails.data));
            
            // Store client_login_base_url in sessionStorage for logout redirect
            // This will be used to redirect directly to client login page, skipping /auth
            const clientLoginBaseUrl = clientConfigDetails.data?.dynamic_config?.client_login_base_url;
            if (clientLoginBaseUrl) {
                sessionStorage.setItem('client_base_url', clientLoginBaseUrl);
                console.log('Stored client_login_base_url in sessionStorage:', clientLoginBaseUrl);
            }
        } else if (clientConfigError) {
            console.error('Client config fetch failed:', clientConfigError);
        }
    }, [clientConfigDetails, clientConfigError, dispatch]);

    // Check if user is authenticated
    if (!isAuthenticated || !user) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <h2>Authentication Required</h2>
                <p>Please log in to access the admin dashboard</p>
            </div>
        );
    }

    if (clientConfigError) {
        console.error('Client config error:', clientConfigError);
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <h2>Error loading configuration</h2>
                <p>Please try refreshing the page</p>
            </div>
        );
    }

    return (
        <Switch>
            {clientConfigLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <Spin size="large" />
                </div>
            )}
            <Suspense
                fallback={
                    <div className="spin">
                        <Spin />
                    </div>
                }
            >
                {/* Dynamic routes */}
                {clientConfigDetails?.data?.dynamic_config?.dynamic_dashboard_tabs?.map(module => {
                    const routePath = module.route_path.startsWith('/') ? module.route_path : `/${module.route_path}`;
                    return (
                        <Route 
                            key={routePath}
                            exact
                            path={`${path}${routePath}`} 
                            render={() => <DynamicIframeRoute config={module} />}
                        />
                    );
                })}

                {/* Static routes */}
                <Route exact path={path} component={Dashboard} />
                <Route path={`${path}/institutes`} component={instituteRoutes} />
                <Route path={`${path}/students`} component={studentRoutes} />
                <Route path={`${path}/leaderboard`} component={LeaderboardRoutes} />
                <Route path={`${path}/allclients`} component={onBoardingRoutes} />
                <Route path={`${path}/addClient/new`} component={addNewClientRoute} />
                <Route path={`${path}/addClient/config`} component={AddNewClientConfig} />
                <Route path={`${path}/client-config`} component={AdminClientConfig} />
                <Route path={`${path}/leads`} component={LeadsTable} />
                <Route path={`${path}/allForms`} component={AllFormTable} />
                <Route path={`${path}/form-library`} component={FormLibrary} />
                <Route path={`${path}/promo_codes`} component={PromoCodesTable} />
                <Route path={`${path}/transactions`} component={TransactionsTable} />
                <Route path={`${path}/utm`} component={UTMRoutes} />
                <Route path={`${path}/bookings`} component={BookingRoute} />
                <Route path={`${path}/flow`} component={FlowRoute} />
                <Route path={`${path}/myresponses`} component={MyResponses} />
                <Route path={`${path}/myprofile`} component={MyProfileRoutes} />
                <Route path={`${path}/campaigns`} component={CampaignsRoutes} />
                <Route path={`${path}/coupon_code`} component={CouponCodeRoutes} />
                <Route path={`${path}/mentor`} component={MentorRoutes} />
                <Route path={`${path}/test_series`} component={TestSeriesRoutes} />
                <Route path={`${path}/addDashboardCards`} component={DashbordMarketingCards} />
                <Route path={`${path}/application_form`} component={ApplicationFormRoutes} />
                <Route path={`${path}/user_responses`} component={UserAssessmentResponsesRoutes} />
            </Suspense>
        </Switch>
    );
}

export default withAdminLayout( Admin );
